import * as React from 'react';
import { clsx } from 'clsx';

export interface ToggleProps {
  /**
   * The label for the toggle
   */
  label?: string;
  /**
   * Whether the toggle is on
   */
  checked?: boolean;
  /**
   * Callback when toggle state changes
   */
  onChange?: (checked: boolean) => void;
  /**
   * Whether the toggle is disabled
   */
  disabled?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
  /**
   * Name attribute for form submission
   */
  name?: string;
  /**
   * Value attribute for form submission
   */
  value?: string;
}

const toggleContainerStyles: React.CSSProperties = {
  width: '40px',
  height: '24px',
  borderRadius: '16px',
  border: '1px solid',
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
};

const knobStyles: React.CSSProperties = {
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  transition: 'all 0.2s ease-in-out',
};

const labelStyles: React.CSSProperties = {
  fontSize: '13px',
  color: '#2f2f2f',
  cursor: 'pointer',
  userSelect: 'none' as const,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const wrapperStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

/**
 * Toggle component - Arbor Design System
 *
 * A toggle switch input with label support.
 */
export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      label,
      checked = false,
      onChange,
      disabled = false,
      className,
      style,
      'data-testid': dataTestId,
      name,
      value,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isActive, setIsActive] = React.useState(false);
    const toggleId = React.useId();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onChange?.(e.target.checked);
      }
    };

    // Container styles based on state
    const getContainerStyles = (): React.CSSProperties => {
      if (disabled) {
        return {
          ...toggleContainerStyles,
          backgroundColor: checked ? '#d1d1d1' : '#ffffff',
          borderColor: checked ? '#d1d1d1' : '#d1d1d1',
          cursor: 'not-allowed',
        };
      }

      if (checked) {
        if (isActive && isFocused) {
          return {
            ...toggleContainerStyles,
            backgroundColor: '#024002',
            borderColor: '#024002',
            boxShadow: '0px 0px 0px 3px #3cad51',
          };
        }
        if (isActive) {
          return {
            ...toggleContainerStyles,
            backgroundColor: '#024002',
            borderColor: '#024002',
          };
        }
        if (isHovered) {
          return {
            ...toggleContainerStyles,
            backgroundColor: '#005700',
            borderColor: '#005700',
          };
        }
        if (isFocused) {
          return {
            ...toggleContainerStyles,
            backgroundColor: '#0b800b',
            borderColor: '#0b800b',
            boxShadow: '0px 0px 0px 3px #3cad51',
          };
        }
        return {
          ...toggleContainerStyles,
          backgroundColor: '#0b800b',
          borderColor: '#0b800b',
        };
      } else {
        if (isActive && isFocused) {
          return {
            ...toggleContainerStyles,
            backgroundColor: '#efefef',
            borderColor: '#2f2f2f',
            boxShadow: '0px 0px 0px 3px #3cad51',
          };
        }
        if (isActive) {
          return {
            ...toggleContainerStyles,
            backgroundColor: '#efefef',
            borderColor: '#2f2f2f',
          };
        }
        if (isHovered) {
          return {
            ...toggleContainerStyles,
            backgroundColor: '#f8f8f8',
            borderColor: '#595959',
          };
        }
        if (isFocused) {
          return {
            ...toggleContainerStyles,
            backgroundColor: '#ffffff',
            borderColor: '#7e7e7e',
            boxShadow: '0px 0px 0px 3px #3cad51',
          };
        }
        return {
          ...toggleContainerStyles,
          backgroundColor: '#ffffff',
          borderColor: '#7e7e7e',
        };
      }
    };

    // Knob styles based on state
    const getKnobStyles = (): React.CSSProperties => {
      const baseKnob = {
        ...knobStyles,
        left: checked ? '18px' : '2px',
      };

      if (disabled) {
        return {
          ...baseKnob,
          backgroundColor: checked ? '#f8f8f8' : '#d1d1d1',
        };
      }

      if (checked) {
        if (isActive) {
          return {
            ...baseKnob,
            backgroundColor: '#efefef',
          };
        }
        if (isHovered) {
          return {
            ...baseKnob,
            backgroundColor: '#f8f8f8',
          };
        }
        return {
          ...baseKnob,
          backgroundColor: '#ffffff',
        };
      } else {
        if (isActive) {
          return {
            ...baseKnob,
            backgroundColor: '#2f2f2f',
          };
        }
        if (isHovered) {
          return {
            ...baseKnob,
            backgroundColor: '#595959',
          };
        }
        return {
          ...baseKnob,
          backgroundColor: '#7e7e7e',
        };
      }
    };

    return (
      <div
        className={clsx('arbor-toggle-wrapper', className)}
        style={{ ...wrapperStyles, ...style }}
        data-testid={dataTestId}
      >
        <input
          ref={ref}
          id={toggleId}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          name={name}
          value={value}
          style={{
            position: 'absolute',
            opacity: 0,
            width: 0,
            height: 0,
          }}
          aria-checked={checked}
        />
        <label
          htmlFor={toggleId}
          onMouseEnter={() => !disabled && setIsHovered(true)}
          onMouseLeave={() => !disabled && setIsHovered(false)}
          onMouseDown={() => !disabled && setIsActive(true)}
          onMouseUp={() => !disabled && setIsActive(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <div style={getContainerStyles()}>
            <div style={getKnobStyles()} />
          </div>
          {label && (
            <span
              style={{
                ...labelStyles,
                color: disabled ? '#7e7e7e' : '#2f2f2f',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {label}
            </span>
          )}
        </label>
      </div>
    );
  }
);

Toggle.displayName = 'Toggle';
